// UI strings for own words (proposals and needs), Finnish (default) and English side by side.
// Finnish is written in full and never truncated; en must have exactly the same shape.
import { defineArea } from '../area';

type IssueLevelKey = 'error' | 'warning' | 'info';

export const ownWordsFi = {
  title: 'Omat sanat',
  modePropose: 'Ehdota sanaa',
  modeNeed: 'Tarvitsemme sanan',
  modeLabel: 'Mitä teet',
  word: 'Sana valyriaksi',
  wordPlaceholder: 'esim. lōgōr',
  pos: 'Sanaluokka',
  posAuto: 'Päättele päätteestä',
  fi: 'Merkitys suomeksi',
  en: 'Merkitys englanniksi',
  construction: 'Miten sana on muodostettu',
  constructionPlaceholder: 'esim. juuri + johdin -ys, mallina …',
  needPlaceholderFi: 'esim. sauna',
  needPlaceholderEn: 'e.g. sauna',
  send: 'Lähetä ehdotus',
  sendNeed: 'Kirjaa tarve',
  meaningRequired: 'Anna merkitys suomeksi tai englanniksi.',
  wordRequired: 'Kirjoita sana.',
  hasErrors: 'Korjaa virheet ennen lähettämistä.',
  sent: 'Lähetetty. Kumppanisi hyväksyy tai hylkää ehdotuksen.',
  needSent: 'Tarve kirjattu. Ehdokkaita etsitään seuraavassa tarkistussessiossa.',
  check: 'Tarkistus',
  checking: 'Tarkistetaan…',
  levels: { error: 'Virhe', warning: 'Huomio', info: 'Lisätieto' } as Record<IssueLevelKey, string>,
  noIssues: 'Ei huomautettavaa: sana näyttää valyrialta.',
  syllables: 'Tavut',
  inferred: 'Taivutusluokka',
  declension: (d: string) => (/^\d+$/.test(d) ? `${d}. deklinaatio` : d),
  adjClass: (c: string) => `${c}-luokan adjektiivi`,
  verbClass: { 'consonant-final': 'konsonanttivartaloinen verbi', 'vowel-final': 'vokaalivartaloinen verbi' } as Record<string, string>,
  model: 'mallisana',
  confidence: { high: 'varma', medium: 'melko varma', low: 'arvaus' } as Record<string, string>,
  clashes: 'Samannäköiset muodot sanakirjassa',
  clashOwn: 'oma muoto',
  table: 'Taivutus (muodostettu wikin säännöistä)',
  noTable: 'Tälle sanaluokalle ei muodosteta taivutustaulukkoa.',
  forYou: 'Odottaa sinun hyväksyntääsi',
  yours: 'Ehdotuksesi, jotka odottavat kumppaniasi',
  needs: 'Tarvitut sanat',
  needsWaiting: 'Odottaa ehdokkaita tarkistussessiosta.',
  picks: 'Valitse ehdokas',
  pickIntro: 'Tarkistussessio ehdotti näitä. Valitsemastasi sanasta tulee ehdotus, jonka toinen teistä hyväksyy.',
  pick: 'Valitse tämä',
  approved: 'Hyväksytyt',
  rejected: 'Hylätyt',
  none: 'Täällä ei ole mitään.',
  by: (name: string) => `ehdotti ${name}`,
  approvedBy: (name: string) => `hyväksyi ${name}`,
  approve: 'Hyväksy',
  suggestChange: 'Ehdota muutosta',
  sendCounter: 'Lähetä muutosehdotus',
  counterNote: 'Muutettu versio palaa ehdottajalle hyväksyttäväksi; sana syntyy vasta, kun toinen teistä hyväksyy sen sellaisenaan.',
  waitingYou: 'Seuraavaksi hyväksyt sinä.',
  waitingPartner: (name: string) => `Seuraavaksi hyväksyy ${name}.`,
  reject: 'Hylkää',
  withdraw: 'Peru ehdotus',
  rejectNote: 'Syy (valinnainen)',
  rejectConfirm: 'Hylkää ehdotus',
  showCheck: 'Näytä tarkistus ja taivutus',
  hideCheck: 'Piilota tarkistus',
  openEntry: 'Avaa sanakirjassa',
  pending: 'Lähettämättä · lähtee, kun yhteys palaa',
  notYours: 'Ehdotuksen hyväksyy se teistä, joka ei sitä tehnyt.',
  noPartner: 'Kumppani ei ole vielä mukana, joten ehdotuksia ei voi hyväksyä.',
  needsClass: 'Sanaluokka puuttuu. Valitse se, niin sana voidaan hyväksyä.',
  chooseClass: 'Valitse sanaluokka',
  pickClass: 'Sanaluokka',
  pickClassNone: 'Valitse ensin sanaluokka.',
  rejectReason: (note: string) => `Syy: ${note}`,

  // ---- suggested inflection (translator phase 4 item 18) ----
  borrowed: 'Lainasana tai nimi',
  borrowedHint:
    'Lainasanat ja vieraat nimet taivutetaan lainasanojen tapaan: -i-loppuiset ja konsonanttiin päättyvät kuuluvat kuudenteen deklinaatioon (oppitunnit 20 ja 29).',
  suggestTitle: 'Ehdotettu taivutus',
  suggestIntro:
    'Sovellus ehdottaa taivutusluokkaa sanan lopun perusteella. Kun hyväksyt sen, sana taivutetaan tämän taulukon mukaan heti, kun kumppanisi hyväksyy ehdotuksen.',
  suggestApprove: 'Hyväksyn tämän taivutuksen',
  suggestNone: 'Tälle sanalle ei ole ehdotettavaa taivutusta.',
  inflection: 'Taivutus',
  inflectionNone: 'Taivutusta ei ole hyväksytty, joten sana ei taivu.',
  inflectionBorrowed: 'lainasanana',
  // ---- layout v2 (docs/design/SIVUPALSTA.md "Omat sanat") ----
  /** The first group, with its count: what waits for this person. */
  waiting: (n: number) => `Odottaa hyväksyntääsi (${n})`,
  nothingWaiting: 'Mikään ei odota hyväksyntääsi.',
  propose: 'Ehdota sanaa',
  proposeTitle: 'Ehdota sanaa',
  history: (n: number) => `Hyväksytyt ja hylätyt (${n})`,
  // ---- the accepted words (Axel 2026-10-09; ownwords/AcceptedWords.tsx) ----
  accepted: (n: number) => `Hyväksytyt sanat (${n})`,
  acceptedSearch: 'Hae sanaa tai merkitystä',
  acceptedNoMatch: 'Haulla ei löytynyt sanoja.',
  acceptedNotYet: 'Tulee sanakirjaan seuraavassa päivityksessä.',
  rejectedHistory: (n: number) => `Hylätyt ehdotukset (${n})`,
};

export const ownWordsEn: typeof ownWordsFi = {
  title: 'Own words',
  modePropose: 'Propose a word',
  modeNeed: 'We need a word',
  modeLabel: 'What you do',
  word: 'Word in High Valyrian',
  wordPlaceholder: 'e.g. lōgōr',
  pos: 'Word class',
  posAuto: 'Infer from the ending',
  fi: 'Meaning in Finnish',
  en: 'Meaning in English',
  construction: 'How the word was built',
  constructionPlaceholder: 'e.g. root + suffix -ys, modelled on …',
  needPlaceholderFi: 'esim. sauna',
  needPlaceholderEn: 'e.g. sauna',
  send: 'Send proposal',
  sendNeed: 'Log the need',
  meaningRequired: 'Give the meaning in Finnish or English.',
  wordRequired: 'Write the word.',
  hasErrors: 'Fix the errors before sending.',
  sent: 'Sent. Your partner approves or rejects the proposal.',
  needSent: 'Need logged. Candidates come from the next review session.',
  check: 'Check',
  checking: 'Checking…',
  levels: { error: 'Error', warning: 'Note', info: 'Info' },
  noIssues: 'Nothing to note: the word looks High Valyrian.',
  syllables: 'Syllables',
  inferred: 'Inflection class',
  declension: (d) => (/^\d+$/.test(d) ? `declension ${d}` : d),
  adjClass: (c) => `class ${c} adjective`,
  verbClass: { 'consonant-final': 'consonant-stem verb', 'vowel-final': 'vowel-stem verb' },
  model: 'model word',
  confidence: { high: 'certain', medium: 'fairly certain', low: 'a guess' },
  clashes: 'Look-alike forms in the dictionary',
  clashOwn: 'own form',
  table: 'Inflection (built from the wiki’s rules)',
  noTable: 'No inflection table is generated for this word class.',
  forYou: 'Waiting for your approval',
  yours: 'Your proposals, waiting for your partner',
  needs: 'Words we need',
  needsWaiting: 'Waiting for candidates from a review session.',
  picks: 'Pick a candidate',
  pickIntro: 'A review session suggested these. Your pick becomes a proposal the other one of you approves.',
  pick: 'Pick this one',
  approved: 'Approved',
  rejected: 'Rejected',
  none: 'Nothing here.',
  by: (name) => `proposed by ${name}`,
  approvedBy: (name) => `approved by ${name}`,
  approve: 'Approve',
  suggestChange: 'Suggest a change',
  sendCounter: 'Send suggested change',
  counterNote: 'The edited version goes back to the proposer for approval; the word is created only when one of you approves it unchanged.',
  waitingYou: 'You approve next.',
  waitingPartner: (name) => `${name} approves next.`,
  reject: 'Reject',
  withdraw: 'Withdraw',
  rejectNote: 'Reason (optional)',
  rejectConfirm: 'Reject proposal',
  showCheck: 'Show check and inflection',
  hideCheck: 'Hide check',
  openEntry: 'Open in the dictionary',
  pending: 'Not sent · sends when you are back online',
  notYours: 'Only the other one of you can approve a proposal.',
  noPartner: 'Your partner has not joined yet, so proposals cannot be approved.',
  needsClass: 'The word class is missing. Choose it so the word can be approved.',
  chooseClass: 'Choose the word class',
  pickClass: 'Word class',
  pickClassNone: 'Choose the word class first.',
  rejectReason: (note) => `Reason: ${note}`,

  borrowed: 'Borrowing or name',
  borrowedHint:
    'Borrowings and foreign names inflect the way borrowings do: those ending in -i or a consonant belong to the sixth declension (lessons 20 and 29).',
  suggestTitle: 'Suggested inflection',
  suggestIntro:
    'The app suggests a class from the end of the word. Once you approve it, the word inflects by this table as soon as your partner approves the proposal.',
  suggestApprove: 'I approve this inflection',
  suggestNone: 'There is no inflection to suggest for this word.',
  inflection: 'Inflection',
  inflectionNone: 'No inflection was approved, so the word does not inflect.',
  inflectionBorrowed: 'as a borrowing',
  waiting: (n) => `Waiting for your approval (${n})`,
  nothingWaiting: 'Nothing is waiting for your approval.',
  propose: 'Propose a word',
  proposeTitle: 'Propose a word',
  history: (n) => `Approved and rejected (${n})`,
  accepted: (n) => `Approved words (${n})`,
  acceptedSearch: 'Search a word or meaning',
  acceptedNoMatch: 'No words match the search.',
  acceptedNotYet: 'Reaches the dictionary with the next update.',
  rejectedHistory: (n) => `Rejected proposals (${n})`,
};

// Messages.ownWords (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('ownWords', ownWordsFi, ownWordsEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof ownWordsFi> {}
}
